import * as React from 'react';
import cx from 'classnames';
import { format } from 'date-fns';
import { useHistory, LinkProps as RouterLinkProps } from 'react-router-dom';
import RouterLink from './RouterLink';
import { addClassName } from '@elevio/styles/lib/utils/classes';
import { getBaseUrl } from '../utils/globals';
import ArticleBody from '../articleBody';
import { IArticle } from '../types';
import useArticle from '../hooks/useArticle';
import useIsArticleActive from '../hooks/useIsArticleActive';
import useArticleFragment from '../hooks/useArticleFragment';

type TitleProps = {
  articleId?: string;
};
/**
 * Render the title of an article, if you don't pass in a articleId
 * then it will use the current article.
 * This will fetch the article if we don't have it already.
 */
export const Title: React.FC<TitleProps> = ({ articleId }) => {
  const article = useArticleFragment(articleId);
  // const article = useArticleFragment();
  return <>{article && article.title}</>;
};

type SummaryProps = {
  className?: string;
  articleId?: string;
};
/**
 * Render the summary of an article, if you don't pass in a articleId
 * then it will use the current article.
 * This will fetch the article if we don't have it already.
 */
export const Summary: React.FC<SummaryProps> = ({ articleId, className }) => {
  const article = useArticleFragment(articleId);
  if (!article || !article.summary) return null;
  return (
    <div className={cx(className, addClassName('article-summary'))}>
      {article.summary}
    </div>
  );
};

type BodyProps = {
  className?: string;
  noStyles?: boolean;
  onClick?: (e: React.MouseEvent) => void;
};
/**
 * Render the body of an article, if you don't pass in a articleId
 * then it will use the current article.
 * This will fetch the article if we don't have it already or we don't have it's body.
 */
export const Body: React.FC<BodyProps> = ({ className, noStyles, onClick }) => {
  const article = useArticle();
  const history = useHistory();
  const baseUrl = getBaseUrl();
  if (!article || !article.body) return null;

  const onClickInterlink = (articleId: string) => {
    // NOTE: the slug is missing, but probably not a big deal for interlinks.
    const url = `${baseUrl}/articles/${articleId}`;
    history.push(url);
  };

  return (
    <ArticleBody
      articleId={article.id}
      className={className}
      html={article.body}
      noStyles={noStyles}
      onClickInterlink={onClickInterlink}
      onClick={onClick}
    />
  );
};

/**
 * Render the last updated time from the current article.
 */
// TODO: this needs localizing
export const LastUpdatedAt = () => {
  const article = useArticle();
  if (!article || !article.lastUpdatedAt) return null;
  return (
    <>
      Last Updated:{' '}
      {format(new Date(article.lastUpdatedAt), 'MMM d, yyyy h:mma')}
    </>
  );
};

// type SnippetProps = {
//   articleId?: string;
// };
// /**
//  * Render the snippet of an article, if you don't pass in a articleId
//  * then it will use the current article.
//  * This will fetch the article if we don't have it already.
//  */
// export const Snippet: React.FC<SnippetProps> = ({ articleId }) => {
//   const article = useArticleFragment(articleId);
//   if (!article || !article.fragment) return null;
//   return <>{article && article.fragment}</>;
// };

type LinkProps = {
  articleId?: string;
  slug?: string;
  'data-testid'?: string;
} & Omit<RouterLinkProps, 'to'>;
/**
 * This will render an A tag with a href that points to an article page.
 * If you pass in a articleId then it will use that otherwise it will
 * use the current article context.
 */
export const Link: React.FC<LinkProps> = ({
  children,
  articleId,
  slug,
  className,
  'data-testid': dataTestId,
  ...props
}) => {
  const baseUrl = getBaseUrl();
  const fragment = useArticleFragment(articleId);
  const _slug = slug ? slug : fragment ? fragment.slug : articleId;
  if (!_slug) return null;

  return (
    <RouterLink
      className={cx(className, addClassName('article-link'))}
      to={`${baseUrl}/articles/${_slug}`}
      data-testid={dataTestId}
      {...props}
    >
      {children}
    </RouterLink>
  );
};

type IsActiveProps = {
  articleId?: string;
  children?: ((isActive: boolean) => React.ReactNode) | React.ReactNode;
};
/**
 * Renders out the child content if the article either from the articleId prop or if not provided the
 * current article is active
 * If a render prop is passed as the child then a boolean will be provided with the is active state
 */
export const IsActive = ({ children, articleId }: IsActiveProps) => {
  const isActive = useIsArticleActive(articleId);
  if (typeof children === 'function') {
    return <>{children(isActive)}</>;
  }
  if (!isActive) return null;
  return <>{children}</>;
};

type IsNotActiveProps = {
  articleId?: string;
  children?: ((isActive: boolean) => React.ReactNode) | React.ReactNode;
};
/**
 * Renders out the child content if the article either from the articleId prop or if not provided the
 * current article is NOT active
 * If a render prop is passed as the child then a boolean will be provided with the is non-active state
 */
export const IsNotActive = ({ children, articleId }: IsNotActiveProps) => {
  const isActive = useIsArticleActive(articleId);
  if (typeof children === 'function') {
    return <>{children(!isActive)}</>;
  }
  if (isActive) return null;
  return <>{children}</>;
};

type ArticleFunction = (data: IArticle) => React.ReactNode;

type ArticleProps = {
  children: ArticleFunction;
};

export const Article = ({ children }: ArticleProps) => {
  const article = useArticle();
  if (!article) return null;
  return <>{children(article)}</>;
};

// export function MetaDescription = () {
// }
